import { LayoutTypeExport } from "@/types/LayoutType";
import { OptionsTypeExport } from "@/types/OptionType";
import { PageTypeError } from "@/types/PageType";
import { Post } from "@/wp/types/client";

export type LocaleTypes = "de_DE" | "en_US";
export type MediaDataType = {
  type: "image" | "video" | "svg" | "animation";
  image_mobile?: ImageType | false;
  image_tablet?: ImageType | false;
  image_desktop: ImageType | false;
  image_fullhd?: ImageType | false;
  video?: MediaVideoType;
  svg?: ImageType | false;
  svg_animation?: FileType | false;
  show_caption?: boolean;
  caption_position?: "has-text-left" | "has-text-right" | "has-text-centered";
  caption?: string;
};

export type MediaVideoType = {
  video_type: "Youtube" | "Vimeo" | "iframe" | "upload";
  externes_video?: string | null;
  externes_video_iframe?: string | null;
  internes_video?: FileType | false;
  video_thumbnail_desktop: ImageType | false;
  audio?: boolean;
};

export type MediaVideoTypeVideoBreakpoints = MediaVideoType & {
  internes_video_desktop: FileType | false;
  internes_video_tablet: FileType | false;
  internes_video_mobile: FileType | false;
};

export type FileType = {
  id: string;
  title: string;
  filename: string;
  url: string;
  link: string;
  alt: string;
  author: string;
  description: string;
  acf?: {
    text_1: "";
    link_1: "";
    text_2: "";
    link_2: "";
    decorative: boolean;
    captions: {
      url: string; //actually FileType but to prevent circular reference
    };
    has_single_page: boolean;
    audio_description_video: FileType | false;
  };
};

export type FetchedFileType = {
  id: string;
  source_url: string; // for videos
  alt_text: string; // for videos
  title: {
    rendered: string; // for videos
  };
  description: {
    rendered: string; // for videos
  };
  caption: {
    rendered: string; // for videos
  };
};

export type ImageType = {
  id: string;
  title: string;
  filename: string;
  url: string;
  link: string;
  alt: string;
  author?: string;
  name: string;
  description: string;
  sizes?: {
    "Preview Icons": string;
    "Preview Icons-height": number;
    "Preview Icons-width": number;
    "Preview Images": string;
    "Preview Images-height": number;
    "Preview Images-width": number;
    large: string;
    "large-height": number;
    "large-width": number;
    medium: string;
    "medium-height": number;
    "medium-width": number;
    medium_large: string;
    "medium_large-height": number;
    "medium_large-width": number;
    thumbnail: string;
    "thumbnail-height": number;
    "thumbnail-width": number;
  };
  date?: string;
  modified?: string;
  mimetype?:
    | "image/jpeg"
    | "image/jpg"
    | "image/webp"
    | "image/png"
    | "image/svg+xml";
  mime_type?:
    | "image/jpeg"
    | "image/jpg"
    | "image/webp"
    | "image/png"
    | "image/svg+xml";
  image_blur: string;
  type?: "image";
  width: number;
  height: number;
  acf: {
    link_1: string;
    link_2: string;
    text_1: string;
    text_2: string;
  };
};

export function isAvailable<T>(data: unknown): data is T {
  return <boolean>data && typeof data !== "boolean";
}

export type MenuType = {
  term_id: number;
  name: string;
  slug: string;
  taxonomy: "nav_menu";
  parent: number;
  count: number;
  items: MenuItemType[];
};

export type MenuItemType = {
  ID?: number;
  menu_order: number;
  type?: string;
  type_label?: string;
  guid?: string;
  url: string;
  title: string;
  slug?: string | null;
  target: string;
  classes: string[];
  post_name: string;
  object?: string | null;
  object_id?: string;
  child_items?: MenuItemType[] | null;
  aria_label?: string;
};

export type LinkType =
  | {
      title: string;
      url: string;
      target: string;
      type?: string;
      aria_label?: string;
    }
  | null
  | ""
  | false;

export type PlatformsType = {
  name?: string;
  link?: string;
  icon?: ImageType;
  type?: "noble-bc" | "noble-elements";
  show_navbar?: boolean;
  show_footer?: boolean;
}[];

export type PlatformType = PlatformsType | false;

export type OptionsType = OptionsTypeExport;

export type CategoryType = {
  id: number;
  count: number;
  link: string;
  name: string;
  slug: string;
  taxonomy: string;
  parent: number;
  meta: {
    overview_page_id: number;
  };
  acf: {
    link: string;
    image: ImageType;
  };
};

export type PostIdType = {
  ID: number;
  post_author: string;
  post_date: string;
  post_date_gmt: string;
  post_content: string;
  post_title: string;
  post_excerpt: string;
  post_status: "publish" | "draft" | "private";
  post_modified: string;
  post_modified_gmt: string;
  parent: number;
  guid: string;
  menu_order: number;
  post_type: string;
  post_mime_type: string;
};

export type PageTypeResponse = PageTypeError | Post;
export type WindowWithAnaylitcs = Window & {
  gtag?: unknown;
  gaData?: unknown;
  _paq?: unknown;
};

export type WPML_TranslationType = {
  locale: LocaleTypes;
  id: number;
  post_title: string;
  href: string;
};

export type LayoutType = LayoutTypeExport;

export type HeadlineData = {
  headline: string;
  ort: string;
  excerpt: string;
};

export type AccordionData = {
  headline: string;
  content: string;
};

export type NextJSStaticPathType = {
  params: { dynamicRoute: string[] };
  locale?: string;
};
export interface SearchType {
  posts: Post[] | null;
  keyword: string | null;
  postsTotal: number;
}

export type ColumnsType = {
  breakpoint:
    | "mobile"
    | "tablet"
    | "desktop"
    | "widescreen"
    | "fullhd"
    | "ultrahd"
    | false;
  number_of_cols: number;
  column_offset?: boolean; // for textmedia modul
};

export type IndexedModuleData<T> = {
  index: number;
  data: T;
}[];

export type ModuleData<
  IndexModuleDataType,
  T extends IndexedModuleData<IndexModuleDataType>,
> = T[number]["data"] | undefined;
